import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { http, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, FileText, Upload, CheckCircle2, X } from "lucide-react";

const DOC_TYPES = [
  { key: "gst_cert", label: "GST Certificate", required: true },
  { key: "fssai_cert", label: "FSSAI License", required: true },
  { key: "ayush_cert", label: "AYUSH License", required: true },
  { key: "pan_card", label: "PAN Card", required: true },
  { key: "coa", label: "Certificate of Analysis (optional)", required: false },
];

export default function VendorRegister() {
  const { user, loading, refresh } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({
    business_name: "", gstin: "", fssai_license: "", ayush_license: "",
    pan: "", bank_account: "", ifsc: "", address: "", description: "",
  });
  const [documents, setDocuments] = useState({}); // {gst_cert: {id, filename, size}, ...}
  const [uploading, setUploading] = useState(null);
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (location.state?.agreement_accepted) setAccepted(true);
  }, [location.state]);

  const uploadDoc = async (docType, file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File exceeds 10MB limit");
      return;
    }
    setUploading(docType);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await http.post(`/vendors/documents/upload?doc_type=${docType}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setDocuments((prev) => ({ ...prev, [docType]: data }));
      toast.success(`${DOC_TYPES.find((d) => d.key === docType)?.label} uploaded`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(null);
    }
  };

  if (loading) return <div className="p-10 text-center">Loading…</div>;
  if (!user) {
    return (
      <div className="max-w-lg mx-auto px-4 py-16 text-center">
        <h1 className="font-serif text-3xl font-bold text-[#1A201C]">Sell on Healthion</h1>
        <p className="mt-2 text-[#5A6B5D]">Please create an account to apply as a vendor.</p>
        <button className="mt-6 btn-forest" onClick={() => nav("/signup")}>Create vendor account</button>
      </div>
    );
  }

  const set = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    if (!accepted) {
      toast.error("Please accept the Marketplace Vendor Agreement to continue");
      return;
    }
    const missing = DOC_TYPES.filter((d) => d.required && !documents[d.key]);
    if (missing.length > 0) {
      toast.error(`Please upload: ${missing.map((m) => m.label).join(", ")}`);
      return;
    }
    setSubmitting(true);
    try {
      const docMap = Object.fromEntries(Object.entries(documents).map(([k, v]) => [k, v.id]));
      await http.post("/vendors/apply", { ...form, agreement_accepted: true, documents: docMap });
      await refresh();
      toast.success("Vendor application submitted for review");
      nav("/vendor");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-10">
      <div className="mb-8">
        <span className="badge-sage inline-flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Step 2 of 2 · KYC & Bank Details</span>
        <h1 className="mt-3 font-serif text-4xl font-bold text-[#1A201C]">Become a Healthion Vendor</h1>
        <p className="mt-2 text-[#5A6B5D]">Amiroop Healthion LLP manually verifies GST, FSSAI and AYUSH credentials before you go live.</p>
        {accepted && (
          <div className="mt-3 inline-flex items-center gap-2 text-xs font-mono text-[#1E3A2B] bg-[#E8F0EA] border border-[#1E3A2B]/20 rounded-full px-3 py-1" data-testid="agreement-accepted-badge">
            <ShieldCheck className="w-3 h-3" /> Marketplace Vendor Agreement accepted
          </div>
        )}
      </div>

      <form onSubmit={submit} className="bg-white border border-[#E2DDD5] rounded-2xl p-6 space-y-5" data-testid="vendor-apply-form">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Business / Brand Name" value={form.business_name} onChange={(v) => set("business_name", v)} testid="v-business" />
          <Field label="GSTIN" value={form.gstin} onChange={(v) => set("gstin", v)} testid="v-gstin" />
          <Field label="FSSAI License Number" value={form.fssai_license} onChange={(v) => set("fssai_license", v)} testid="v-fssai" />
          <Field label="AYUSH License Number" value={form.ayush_license} onChange={(v) => set("ayush_license", v)} testid="v-ayush" />
          <Field label="PAN" value={form.pan} onChange={(v) => set("pan", v)} testid="v-pan" />
          <Field label="Bank Account Number" value={form.bank_account} onChange={(v) => set("bank_account", v)} testid="v-bank" />
          <Field label="IFSC Code" value={form.ifsc} onChange={(v) => set("ifsc", v)} testid="v-ifsc" />
        </div>
        <Field label="Registered Address" value={form.address} onChange={(v) => set("address", v)} testid="v-address" />
        <div>
          <label className="text-xs font-mono uppercase text-[#5A6B5D]">Brand Story / Description</label>
          <textarea
            required
            value={form.description}
            onChange={(e) => set("description", e.target.value)}
            className="mt-1 w-full border border-[#E2DDD5] rounded-lg p-3 text-sm min-h-[100px]"
            placeholder="Tell customers what makes your Ayurvedic brand special..."
            data-testid="v-description"
          />
        </div>

        <div className="border-t border-[#E2DDD5] pt-5">
          <div className="text-xs font-mono uppercase text-[#5A6B5D] mb-1">KYC Document Uploads</div>
          <h3 className="font-serif text-xl font-semibold text-[#1E3A2B]">Upload license copies</h3>
          <p className="text-xs text-[#5A6B5D] mt-1">PDF, JPG, PNG or WEBP · Max 10 MB each. Documents are stored securely for Amiroop Healthion LLP verification only.</p>

          <div className="mt-4 grid sm:grid-cols-2 gap-3">
            {DOC_TYPES.map((d) => {
              const uploaded = documents[d.key];
              const busy = uploading === d.key;
              return (
                <div key={d.key} className="bg-[#FAF8F5] border border-[#E2DDD5] rounded-xl p-3" data-testid={`doc-row-${d.key}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-[#1A201C]">{d.label}{d.required && <span className="text-[#D97706]"> *</span>}</div>
                      {uploaded ? (
                        <div className="mt-1 flex items-center gap-1 text-xs text-[#1E3A2B] font-mono truncate">
                          <CheckCircle2 className="w-3 h-3 shrink-0" /> <span className="truncate">{uploaded.filename}</span>
                        </div>
                      ) : (
                        <div className="mt-1 text-xs text-[#5A6B5D]">Not uploaded</div>
                      )}
                    </div>
                    {uploaded && (
                      <button type="button" onClick={() => setDocuments((p) => { const n = { ...p }; delete n[d.key]; return n; })} className="text-[#5A6B5D] hover:text-red-600" data-testid={`doc-remove-${d.key}`}>
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <label className={`mt-2 cursor-pointer inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border ${uploaded ? "border-[#E2DDD5] text-[#5A6B5D]" : "bg-[#1E3A2B] text-white border-[#1E3A2B]"} ${busy ? "opacity-60" : ""}`}>
                    <Upload className="w-3 h-3" />
                    {busy ? "Uploading…" : uploaded ? "Replace" : "Upload"}
                    <input
                      type="file"
                      accept="application/pdf,image/jpeg,image/png,image/webp"
                      onChange={(e) => uploadDoc(d.key, e.target.files?.[0])}
                      className="hidden"
                      disabled={busy}
                      data-testid={`doc-input-${d.key}`}
                    />
                  </label>
                </div>
              );
            })}
          </div>
        </div>
        <div className="bg-[#E8F0EA] border border-[#1E3A2B]/20 rounded-xl p-4 text-xs text-[#1E3A2B]">
          By submitting you agree to Amiroop Healthion LLP's 12% marketplace commission, T+7 to T+10 payout cycle, and doctor moderation of all product listings.
        </div>

        <label className="flex items-start gap-3 p-4 bg-[#FAF8F5] border border-[#E2DDD5] rounded-xl cursor-pointer" data-testid="agreement-accept-row">
          <input
            type="checkbox"
            checked={accepted}
            onChange={(e) => setAccepted(e.target.checked)}
            className="mt-0.5 w-4 h-4 accent-[#1E3A2B]"
            data-testid="agreement-accept-checkbox"
          />
          <span className="text-sm text-[#1A201C]">
            I have read and accept the{" "}
            <Link to="/vendor/agreement" target="_blank" className="text-[#1E3A2B] font-semibold underline inline-flex items-center gap-1" data-testid="agreement-link">
              <FileText className="w-3.5 h-3.5" /> Marketplace Vendor Agreement
            </Link>{" "}
            on behalf of my business, including compliance, commission, indemnity and jurisdiction clauses.
          </span>
        </label>
        <button disabled={submitting} className="w-full btn-forest" data-testid="submit-vendor-application">
          {submitting ? "Submitting..." : "Submit Application for Review"}
        </button>
      </form>
    </div>
  );
}

function Field({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="text-xs font-mono uppercase text-[#5A6B5D]">{label}</label>
      <input
        required
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full border border-[#E2DDD5] rounded-lg p-2.5 text-sm bg-white"
        data-testid={testid}
      />
    </div>
  );
}
